<template>
  <div class="mt-4">
    <v-alert v-if="lastError" dense type="error" outlined dismissible class="mx-auto mb-2" max-width="95%"
      @input="lastError = ''">
      <strong>API 호출 실패:</strong>
      <div style="font-size:11px;white-space:pre-wrap;">{{ lastError }}</div>
    </v-alert>
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark dense color="indigo darken-2">
        <v-toolbar-title>
          <v-icon left small>mdi-calendar-month</v-icon>월별 통계 (MAU 정확)
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-tabs v-model="tab" dark dense background-color="transparent" slider-color="amber">
          <v-tab>매체별</v-tab>
          <v-tab>게임별</v-tab>
        </v-tabs>
      </v-toolbar>
      <v-card-text>
        <v-row dense>
          <v-col cols="6" md="3">
            <v-text-field dense outlined hide-details type="month" label="From (YYYY-MM)" v-model="fromYm"></v-text-field>
          </v-col>
          <v-col cols="6" md="3">
            <v-text-field dense outlined hide-details type="month" label="To (YYYY-MM)" v-model="toYm"></v-text-field>
          </v-col>
          <v-col cols="6" md="3" v-if="tab===0">
            <v-autocomplete dense outlined hide-details clearable
              label="매체 (옵션)"
              :items="mediaItems" :loading="loadingMedia"
              item-text="label" item-value="app_key"
              v-model="filterAppKey"
              no-data-text="매체 없음" placeholder="검색 가능"></v-autocomplete>
          </v-col>
          <v-col cols="6" md="3" v-if="tab===1">
            <v-autocomplete dense outlined hide-details clearable
              label="게임 (옵션)"
              :items="gameItems" :loading="loadingGames"
              item-text="label" item-value="game_id"
              v-model="filterGameId"
              no-data-text="게임 없음" placeholder="검색 가능"></v-autocomplete>
          </v-col>
          <v-col cols="6" md="3">
            <v-btn color="primary" :loading="loading" @click="load" block>조회</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 월별 막대 차트 -->
    <v-card class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-toolbar dark dense color="teal darken-2">
        <v-toolbar-title>월별 추이 ({{ chartMetric }})</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn-toggle v-model="chartMetric" mandatory dense color="amber accent-2">
          <v-btn x-small value="total_bet_amount" dark>베팅금</v-btn>
          <v-btn x-small value="bet_count" dark>베팅수</v-btn>
          <v-btn x-small value="active_user_count" dark v-if="tab===0">MAU</v-btn>
          <v-btn x-small value="participant_count" dark v-if="tab===1">참여자</v-btn>
        </v-btn-toggle>
      </v-toolbar>
      <v-card-text>
        <bar-chart v-if="chartData.datasets.length" :chart-data="chartData" :options="chartOptions" :height="80"></bar-chart>
        <div v-else class="text-center grey--text pa-6">데이터 없음</div>
      </v-card-text>
    </v-card>

    <!-- 매체별 -->
    <v-card v-if="tab===0" class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-toolbar dark dense color="indigo">
        <v-toolbar-title>매체별 월간 데이터</v-toolbar-title>
        <v-spacer></v-spacer>
        <span class="text-caption white--text">{{ mediaRows.length }} rows</span>
      </v-toolbar>
      <v-data-table dense :headers="mediaHeaders" :items="mediaRows" :items-per-page="50" :loading="loading">
        <template v-slot:item.total_bet_amount="{ item }">{{ fmt(item.total_bet_amount) }}</template>
        <template v-slot:item.total_payout_amount="{ item }">{{ fmt(item.total_payout_amount) }}</template>
        <template v-slot:item.net_revenue="{ item }">
          <span :class="item.net_revenue >= 0 ? 'green--text' : 'red--text'">{{ fmt(item.net_revenue) }}</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- 게임별 -->
    <v-card v-if="tab===1" class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-toolbar dark dense color="indigo">
        <v-toolbar-title>게임별 월간 데이터</v-toolbar-title>
        <v-spacer></v-spacer>
        <span class="text-caption white--text">{{ gameRows.length }} rows</span>
      </v-toolbar>
      <v-data-table dense :headers="gameHeaders" :items="gameRows" :items-per-page="50" :loading="loading">
        <template v-slot:item.total_bet_amount="{ item }">{{ fmt(item.total_bet_amount) }}</template>
        <template v-slot:item.total_payout="{ item }">{{ fmt(item.total_payout) }}</template>
        <template v-slot:item.margin_rate_calc="{ item }">
          <v-chip x-small :color="marginColor(marginRate(item))" dark>{{ pctOrDash(marginRate(item)) }}</v-chip>
        </template>
        <template v-slot:item.refund_round_rate="{ item }">
          <v-chip x-small :color="refundColor(refundRate(item))" :dark="refundRate(item) > 0">
            {{ pctOrDash(refundRate(item)) }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card>

    <div class="py-12"></div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import BarChart from './charts/BarChart.js';

function curYm() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function shiftYm(ym, deltaMonths) {
  let [y, m] = ym.split('-').map(Number);
  m += deltaMonths;
  while (m < 1) { m += 12; y -= 1; }
  while (m > 12) { m -= 12; y += 1; }
  return `${y}-${String(m).padStart(2, '0')}`;
}

export default {
  name: 'Danta_Stats_Monthly',
  components: { BarChart },
  data() {
    return {
      tab: 0,
      loading: false,
      lastError: '',
      fromYm: shiftYm(curYm(), -5),
      toYm:   curYm(),
      filterAppKey: '',
      filterGameId: '',
      mediaRows: [],
      gameRows: [],
      mediaItems: [],
      gameItems: [],
      loadingMedia: false,
      loadingGames: false,
      chartMetric: 'total_bet_amount',
      mediaHeaders: [
        { text: '월', value: 'year_month' },
        { text: '매체ID', value: 'media_id' },
        { text: 'app_key', value: 'app_key' },
        { text: 'MAU', value: 'active_user_count', align: 'right' },
        { text: '신규', value: 'new_user_count', align: 'right' },
        { text: '베팅', value: 'bet_count', align: 'right' },
        { text: '취소', value: 'cancelled_count', align: 'right' },
        { text: '베팅금', value: 'total_bet_amount', align: 'right' },
        { text: '지급액', value: 'total_payout_amount', align: 'right' },
        { text: '순수익', value: 'net_revenue', align: 'right' },
      ],
      gameHeaders: [
        { text: '월', value: 'year_month' },
        { text: '게임ID', value: 'game_id', align: 'right' },
        { text: '게임명', value: 'game_title' },
        { text: '템플릿', value: 'template_code' },
        { text: '라운드', value: 'round_count', align: 'right' },
        { text: '참여자', value: 'participant_count', align: 'right' },
        { text: '베팅수', value: 'bet_count', align: 'right' },
        { text: '베팅금', value: 'total_bet_amount', align: 'right' },
        { text: '지급액', value: 'total_payout', align: 'right' },
        { text: '마진율', value: 'margin_rate_calc', align: 'right' },
        { text: 'UP승', value: 'up_win_count', align: 'right' },
        { text: 'DOWN승', value: 'down_win_count', align: 'right' },
        { text: 'SAME', value: 'same_count', align: 'right' },
        { text: '취소', value: 'cancelled_count', align: 'right' },
        { text: '환불%', value: 'refund_round_rate', align: 'right' },
      ],
    };
  },
  computed: {
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: { yAxes: [{ ticks: { beginAtZero: true } }] },
        legend: { display: true },
      };
    },
    chartData() {
      const rows = this.tab === 0 ? this.mediaRows : this.gameRows;
      const byYm = new Map();
      for (const r of rows) {
        const ym = r.year_month;
        const v = Number(r[this.chartMetric] || 0);
        byYm.set(ym, (byYm.get(ym) || 0) + v);
      }
      const labels = Array.from(byYm.keys()).sort();
      const values = labels.map(l => byYm.get(l));
      if (labels.length === 0) return { labels: [], datasets: [] };
      return {
        labels,
        datasets: [{
          label: this.chartMetric,
          backgroundColor: '#26a69a',
          data: values,
        }],
      };
    },
  },
  created() {
    this.load();
    this.loadMediaList();
    this.loadGameList();
  },
  methods: {
    ...mapActions(['DANTA_STATS_MONTHLY_MEDIA', 'DANTA_STATS_MONTHLY_GAMES', 'DANTA_GET_MEDIA_LIST', 'DANTA_GET_GAME_LIST']),
    fmt(n) { return n == null ? '-' : Number(n).toLocaleString(); },
    pctOrDash(rate) {
      if (rate == null) return '-';
      return `${(Number(rate) * 100).toFixed(1)}%`;
    },
    marginRate(item) {
      const bet = Number(item.total_bet_amount || 0);
      if (!bet) return null;
      const pay = Number(item.total_payout || 0);
      return (bet - pay) / bet;
    },
    refundRate(item) {
      const r = Number(item.round_count || 0);
      if (!r) return null;
      return (Number(item.same_count || 0) + Number(item.cancelled_count || 0)) / r;
    },
    marginColor(m) {
      if (m == null) return 'grey';
      if (m < 0)     return 'red';
      if (m < 0.01)  return 'orange';
      return 'green';
    },
    refundColor(r) {
      if (r == null || r === 0) return 'grey lighten-2';
      if (r >= 0.5)              return 'red';
      if (r >= 0.2)              return 'orange';
      return 'amber';
    },
    async loadMediaList() {
      this.loadingMedia = true;
      try {
        const r = await this.DANTA_GET_MEDIA_LIST({ client_id: null });
        const rows = (r && r.media_list) || [];
        this.mediaItems = rows.map(m => ({
          app_key: m.app_key,
          label: `${m.media_name || m.media_id || m.app_key}  [${m.app_key}]`,
        }));
      } catch (err) { console.warn('media list load 실패:', err); }
      finally { this.loadingMedia = false; }
    },
    async loadGameList() {
      this.loadingGames = true;
      try {
        const r = await this.DANTA_GET_GAME_LIST({ page: 1, page_size: 500 });
        const rows = (r && r.game_list) || [];
        this.gameItems = rows.map(g => ({
          game_id: Number(g.game_id),
          label: `${g.title || g.game_code || '(이름없음)'}  [#${g.game_id}]`,
        }));
      } catch (err) { console.warn('game list load 실패:', err); }
      finally { this.loadingGames = false; }
    },
    async load() {
      this.loading = true;
      try {
        if (this.tab === 0) {
          const r = await this.DANTA_STATS_MONTHLY_MEDIA({
            from: this.fromYm, to: this.toYm,
            app_key: this.filterAppKey || undefined,
          });
          this.mediaRows = (r && r.rows) || [];
        } else {
          const r = await this.DANTA_STATS_MONTHLY_GAMES({
            from: this.fromYm, to: this.toYm,
            game_id: this.filterGameId || undefined,
          });
          this.gameRows = (r && r.rows) || [];
        }
        this.lastError = '';
      } catch (err) {
        this.lastError = (err && (err.message || err.err_message)) || String(err);
        console.error('[Stats_Monthly] load 실패:', err);
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    tab() { this.load(); },
  },
};
</script>
